import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CheckCircle, Warning, ArrowsClockwise } from "@phosphor-icons/react";

// Every marketing-item field that the sync writes to. Mapping value is a
// comma-separated fallback list of SharePoint internal column names.
const APP_FIELDS = [
  { key: "title", label: "Title", required: true, help: "Falls back to LinkTitle / Name." },
  { key: "classification", label: "Classification", help: "Article, Podcast, Campaign, LinkedIn, Video…" },
  { key: "provided_by", label: "Provided by" },
  { key: "current_status", label: "Current status" },
  { key: "description", label: "Description / body" },
  { key: "author_name", label: "Author name" },
  { key: "author_email", label: "Author email" },
  { key: "author_department", label: "Author department" },
  { key: "campaign_description", label: "Campaign description" },
  { key: "campaign_goal", label: "Campaign goal" },
  { key: "campaign_result", label: "Campaign result" },
  { key: "published_at", label: "Published date" },
  { key: "campaign_end_at", label: "Campaign end date" },
];

export default function IntegrationsTab({ settings, onSaved }) {
  const [form, setForm] = useState({
    sharepoint_hostname: settings.sharepoint_hostname || "",
    sharepoint_site_path: settings.sharepoint_site_path || "",
    sharepoint_list_id: settings.sharepoint_list_id || "",
    sharepoint_column_map: { ...(settings.sharepoint_column_map || {}) },
    sharepoint_access_upn: settings.sharepoint_access_upn || "",
    onedrive_root_folder: settings.onedrive_root_folder || "",
    onedrive_owner_upn: settings.onedrive_owner_upn || "",
  });
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [users, setUsers] = useState([]);

  useEffect(() => {
    setForm({
      sharepoint_hostname: settings.sharepoint_hostname || "",
      sharepoint_site_path: settings.sharepoint_site_path || "",
      sharepoint_list_id: settings.sharepoint_list_id || "",
      sharepoint_column_map: { ...(settings.sharepoint_column_map || {}) },
      sharepoint_access_upn: settings.sharepoint_access_upn || "",
      onedrive_root_folder: settings.onedrive_root_folder || "",
      onedrive_owner_upn: settings.onedrive_owner_upn || "",
    });
  }, [settings]);

  useEffect(() => {
    api.get("/users").then((r) => setUsers(r.data || [])).catch(() => setUsers([]));
  }, []);

  const licensedUsers = useMemo(
    () => {
      const base = users
        .filter((u) => u.is_licensed !== false && u.role !== "guest")
        .map((u) => ({
          upn: (u.user_principal_name || u.email || "").toLowerCase(),
          name: u.name || u.email,
          email: u.email,
          synthetic: false,
        }))
        .filter((u) => u.upn);
      // Guarantee the Sender mailbox + currently-selected UPNs are always
      // pickable — even if the last Microsoft user sync ran before those
      // accounts were licensed (common for shared mailboxes flipped to
      // licensed users). Prevents "why isn't leads@ in the list?" surprise.
      const extra = [
        (settings.email_sender_address || "").toLowerCase(),
        (form.onedrive_owner_upn || "").toLowerCase(),
        (form.sharepoint_access_upn || "").toLowerCase(),
      ].filter(Boolean);
      const seen = new Set(base.map((u) => u.upn));
      for (const upn of extra) {
        if (!seen.has(upn)) {
          base.push({ upn, name: upn, email: upn, synthetic: true });
          seen.add(upn);
        }
      }
      return base.sort((a, b) => a.name.localeCompare(b.name));
    },
    [users, settings.email_sender_address, form.onedrive_owner_upn, form.sharepoint_access_upn],
  );

  const [syncingUsers, setSyncingUsers] = useState(false);
  const resyncUsers = async () => {
    setSyncingUsers(true);
    try {
      const { data } = await api.post("/users/sync-microsoft");
      const users2 = await api.get("/users");
      setUsers(users2.data || []);
      toast.success(`Synced ${data.total || 0} users (${data.inserted || 0} new, ${data.updated || 0} updated)`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Sync failed");
    } finally { setSyncingUsers(false); }
  };

  const setField = (k, v) => setForm((s) => ({ ...s, [k]: v }));
  const setMapField = (k, v) => setForm((s) => ({ ...s, sharepoint_column_map: { ...s.sharepoint_column_map, [k]: v } }));

  // Preview: {owner}/{root}/{item_id}/{filename} — mirrors the actual upload logic.
  const previewOwner = form.onedrive_owner_upn || settings.email_sender_address || "leads@barnardinc.co.za";
  const previewRoot = (form.onedrive_root_folder || "Marketing").replace(/^\/+|\/+$/g, "");

  const save = async () => {
    setSaving(true);
    try {
      await api.patch("/settings", form);
      toast.success("Integration settings saved");
      onSaved?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const test = async () => {
    // Save first so the backend probes the values the admin just typed.
    setTesting(true);
    setTestResult(null);
    try {
      await api.patch("/settings", form);
      const { data } = await api.post("/marketing/sharepoint/test");
      setTestResult(data);
      if (data.ok) toast.success(`Connected — read ${data.sample_count} sample rows`);
      else toast.error("SharePoint test failed — see details below");
      onSaved?.();
    } catch (err) {
      const detail = err?.response?.data?.detail || err?.message || "Test failed";
      setTestResult({ ok: false, error: detail });
      toast.error(detail);
    } finally {
      setTesting(false);
    }
  };

  return (
    <div className="space-y-10 max-w-4xl" data-testid="integrations-tab">
      <div>
        <div className="label-tracking mb-1">Marketing sources</div>
        <h2 className="font-display text-2xl text-slate-900">SharePoint list &amp; OneDrive storage</h2>
        <p className="text-sm text-slate-500 mt-2 max-w-2xl">
          These values drive the Marketing → <em>Resync SharePoint</em> button and where uploaded articles/podcasts land in OneDrive. Defaults match the .iqy list supplied at setup.
        </p>
      </div>

      {/* SharePoint site */}
      <section className="space-y-4">
        <div className="label-tracking">SharePoint list source</div>
        <div>
          <Label className="text-xs text-slate-600">Access user (whose Entra rights govern this connection)</Label>
          <Select value={form.sharepoint_access_upn || "__none__"} onValueChange={(v) => setField("sharepoint_access_upn", v === "__none__" ? "" : v)}>
            <SelectTrigger data-testid="sp-access-upn" className="mt-1 rounded-sm h-9 text-sm font-mono">
              <SelectValue placeholder="Pick a licensed user…"/>
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__none__">— None (app-only access) —</SelectItem>
              {licensedUsers.map((u) => (
                <SelectItem key={u.upn} value={u.upn}>{u.name}{u.synthetic ? " · (not synced yet)" : ""} · {u.upn}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <p className="text-[11px] text-amber-800 mt-1 bg-amber-50 border border-amber-200 rounded-sm px-2 py-1.5">
            <span className="font-medium">Note:</span> Picking a user here does <em>not</em> grant the sync access — Graph runs as the Azure app registration, not as this user. This field is a governance/audit label. If the connection test fails with 403, the fix is in Entra (see the error panel).
          </p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label className="text-xs text-slate-600">Hostname</Label>
            <Input
              data-testid="sp-hostname"
              value={form.sharepoint_hostname}
              onChange={(e) => setField("sharepoint_hostname", e.target.value)}
              placeholder="barnardinc.sharepoint.com"
              className="mt-1 rounded-sm h-9 text-sm font-mono"
            />
          </div>
          <div>
            <Label className="text-xs text-slate-600">Site path</Label>
            <Input
              data-testid="sp-site-path"
              value={form.sharepoint_site_path}
              onChange={(e) => setField("sharepoint_site_path", e.target.value)}
              placeholder="/sites/BarnardIncInTheKnow"
              className="mt-1 rounded-sm h-9 text-sm font-mono"
            />
            <p className="text-[11px] text-slate-500 mt-1">Must begin with <code>/sites/</code>…</p>
          </div>
          <div className="md:col-span-2">
            <Label className="text-xs text-slate-600">List ID (GUID)</Label>
            <Input
              data-testid="sp-list-id"
              value={form.sharepoint_list_id}
              onChange={(e) => setField("sharepoint_list_id", e.target.value)}
              placeholder="50bcec86-8409-49c0-a5c5-6432b61560fe"
              className="mt-1 rounded-sm h-9 text-sm font-mono"
            />
            <p className="text-[11px] text-slate-500 mt-1">Find it in the SharePoint list URL <code>?List=&#123;GUID&#125;</code> or in the .iqy file.</p>
          </div>
        </div>
      </section>

      {/* Column mapping */}
      <section className="space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <div className="label-tracking">Column mapping</div>
            <p className="text-xs text-slate-500 mt-1">Comma-separated fallbacks are supported — the first SharePoint column that returns a value wins.</p>
          </div>
        </div>
        <div className="border border-slate-200 rounded-sm divide-y divide-slate-100">
          {APP_FIELDS.map(({ key, label, help }) => (
            <div key={key} className="grid grid-cols-12 gap-3 items-center px-3 py-2.5">
              <div className="col-span-12 md:col-span-4">
                <div className="text-sm text-slate-900">{label}</div>
                {help && <div className="text-[11px] text-slate-500">{help}</div>}
              </div>
              <div className="col-span-12 md:col-span-8">
                <Input
                  data-testid={`sp-map-${key}`}
                  value={form.sharepoint_column_map[key] || ""}
                  onChange={(e) => setMapField(key, e.target.value)}
                  placeholder="SharePointInternalName"
                  className="rounded-sm h-9 text-sm font-mono"
                />
                {testResult?.ok && testResult.detected_columns?.[key] === null && (
                  <div className="text-[11px] text-amber-700 mt-1 inline-flex items-center gap-1"><Warning size={12}/> None of these columns exist in the sample row.</div>
                )}
                {testResult?.ok && testResult.detected_columns?.[key] && (
                  <div className="text-[11px] text-emerald-700 mt-1 inline-flex items-center gap-1"><CheckCircle size={12}/> Detected: <code>{testResult.detected_columns[key]}</code></div>
                )}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* OneDrive */}
      <section className="space-y-4">
        <div className="label-tracking">OneDrive storage</div>
        <div>
          <Label className="text-xs text-slate-600">Storage owner (which user's OneDrive)</Label>
          <Select value={form.onedrive_owner_upn || "__default__"} onValueChange={(v) => setField("onedrive_owner_upn", v === "__default__" ? "" : v)}>
            <SelectTrigger data-testid="od-owner-upn" className="mt-1 rounded-sm h-9 text-sm font-mono">
              <SelectValue/>
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__default__">— Use Sender mailbox ({settings.email_sender_address || "leads@barnardinc.co.za"}) —</SelectItem>
              {licensedUsers.map((u) => (
                <SelectItem key={u.upn} value={u.upn}>{u.name}{u.synthetic ? " · (not synced yet)" : ""} · {u.upn}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <div className="flex items-center justify-between mt-1">
            <p className="text-[11px] text-slate-500">
              Don't see an account? Recently-licensed users (e.g. a shared mailbox flipped to a licensed one) only appear after the next Microsoft sync.
            </p>
            <Button
              data-testid="resync-users-btn"
              type="button"
              variant="ghost"
              size="sm"
              onClick={resyncUsers}
              disabled={syncingUsers}
              className="h-7 text-[11px] gap-1 text-slate-600 hover:text-slate-900"
            >
              <ArrowsClockwise size={12} className={syncingUsers ? "animate-spin" : ""}/>
              {syncingUsers ? "Syncing…" : "Sync users"}
            </Button>
          </div>
        </div>
        <div>
          <Label className="text-xs text-slate-600">Root folder</Label>
          <Input
            data-testid="od-root"
            value={form.onedrive_root_folder}
            onChange={(e) => setField("onedrive_root_folder", e.target.value)}
            placeholder="Marketing"
            className="mt-1 rounded-sm h-9 text-sm font-mono max-w-xs"
          />
        </div>
        <div className="bg-slate-50 border border-slate-200 rounded-sm p-3 text-xs" data-testid="od-preview">
          <div className="label-tracking mb-1">Resulting storage path</div>
          <div className="font-mono text-slate-800 break-all">
            <span className="text-slate-500">OneDrive of</span> {previewOwner}
            <span className="text-slate-400"> / </span>
            <span className="text-slate-900">{previewRoot}</span>
            <span className="text-slate-400"> / </span>
            <span className="text-slate-500">&#123;item_id&#125;</span>
            <span className="text-slate-400"> / </span>
            <span className="text-slate-500">&#123;filename&#125;</span>
          </div>
          <p className="text-[11px] text-slate-500 mt-2">
            The <code>{previewRoot}</code> folder and per-item subfolders are created automatically on the first upload — no manual bootstrap required.
          </p>
        </div>
      </section>

      {/* Test result */}
      {testResult && (
        <section data-testid="sp-test-result" className={`rounded-sm border p-4 text-sm ${testResult.ok ? "bg-emerald-50 border-emerald-200" : "bg-rose-50 border-rose-200"}`}>
          {testResult.ok ? (
            <>
              <div className="inline-flex items-center gap-1 font-medium text-emerald-800">
                <CheckCircle size={14}/> Connection OK
              </div>
              <div className="mt-1 text-emerald-900 text-xs">
                Read {testResult.sample_count} sample rows from <code>{testResult.site?.hostname}{testResult.site?.path}</code>.
              </div>
              {testResult.sample_row_keys?.length > 0 && (
                <details className="mt-2 text-xs">
                  <summary className="cursor-pointer text-slate-600">Available SharePoint columns ({testResult.sample_row_keys.length})</summary>
                  <div className="mt-1 font-mono text-slate-700 flex flex-wrap gap-1">
                    {testResult.sample_row_keys.map((k) => (
                      <span key={k} className="bg-white border border-slate-200 rounded-sm px-1.5 py-0.5">{k}</span>
                    ))}
                  </div>
                </details>
              )}
            </>
          ) : (
            <>
              <div className="inline-flex items-center gap-1 font-medium text-rose-800">
                <Warning size={14}/> {testResult.stage ? `Failed at ${testResult.stage} stage` : "Test failed"}
              </div>
              <div className="mt-1 text-rose-900 text-xs whitespace-pre-wrap break-all">{testResult.error}</div>
              {testResult.hint && (
                <div className="mt-2 text-xs text-rose-900 border-t border-rose-200 pt-2">
                  <span className="font-medium">Hint:</span> {testResult.hint}
                </div>
              )}
              {/^.*403|accessDenied|Forbidden/.test(testResult.error || "") && (
                <div className="mt-3 border-t border-rose-200 pt-3 space-y-3 text-xs text-rose-950">
                  <div>
                    <div className="font-semibold text-rose-900 mb-1">Why a user with list access doesn't unblock this</div>
                    <p>
                      SharePoint sync runs under the Graph <em>client-credentials</em> flow — it authenticates as the Azure app registration itself, not as any human user. Giving Jaco (or anyone) access to the list only affects <em>their</em> browser session; the app has its own separate permission grants and currently has none for this list.
                    </p>
                  </div>
                  {settings.azure_client_id && (
                    <div className="bg-white border border-rose-200 rounded-sm p-2 font-mono">
                      <span className="text-slate-500">App (client) ID: </span>
                      <span className="text-slate-900">{settings.azure_client_id}</span>
                    </div>
                  )}
                  <div>
                    <div className="font-semibold text-rose-900 mb-1">Fix — pick ONE (Azure admin, one-time)</div>
                    <ol className="list-decimal pl-5 space-y-2">
                      <li>
                        <span className="font-medium">Simple:</span> Entra ID → App registrations → this app → API permissions → <em>Add</em> → Microsoft Graph → <em>Application permissions</em> → search <code>Sites.Read.All</code> → <em>Grant admin consent for barnardinc.co.za</em>. Broad but done in 30 seconds.
                      </li>
                      <li>
                        <span className="font-medium">Least-privilege:</span> Grant the app <code>Sites.Selected</code> (Application), admin-consent it, then a site owner runs the Graph call below to give this specific site read-only access.
                        <pre className="mt-1 bg-slate-900 text-slate-100 rounded-sm p-2 whitespace-pre-wrap text-[11px] leading-snug">{`POST https://graph.microsoft.com/v1.0/sites/${testResult.site?.hostname || form.sharepoint_hostname}:${testResult.site?.path || form.sharepoint_site_path}:/permissions
Content-Type: application/json

{
  "roles": ["read"],
  "grantedToIdentities": [
    { "application": { "id": "${settings.azure_client_id || "<app-client-id>"}", "displayName": "Barnard Lead Tracker" } }
  ]
}`}</pre>
                      </li>
                    </ol>
                  </div>
                  <div className="text-rose-800">
                    After granting + admin consent, wait ~2 minutes (Azure caches), then click <em>Test connection</em> again.
                  </div>
                </div>
              )}
            </>
          )}
        </section>
      )}

      {/* Actions */}
      <div className="flex flex-wrap items-center gap-3">
        <Button data-testid="integrations-save" onClick={save} disabled={saving} className="rounded-sm bg-slate-900 hover:bg-slate-800">
          {saving ? "Saving…" : "Save changes"}
        </Button>
        <Button data-testid="integrations-test" onClick={test} disabled={testing} variant="outline" className="rounded-sm gap-2 border-slate-300 hover:bg-slate-900 hover:text-white hover:border-slate-900">
          <ArrowsClockwise size={14} className={testing ? "animate-spin" : ""}/>
          {testing ? "Testing…" : "Test connection"}
        </Button>
      </div>
    </div>
  );
}
